import { useMemo, useContext, ReactElement } from 'react';
import { SplitFactoryProvider } from '@splitsoftware/splitio-react';
import { datadogRum } from '@datadog/browser-rum';

import { getOscConfig } from '@twiliointernal/e2-commons-shared-definitions';
import { logger, Segment } from '@twiliointernal/e2-commons-shared-libs';

import { UserContext } from '../userProvider';

const { splitAuthorizationKey } = getOscConfig();

export const ExperimentProvider = ({ children }): ReactElement => {
  const { account } = useContext(UserContext);

  // If sub-account, use parentAccountSid
  const isSubAccount = account?.parentAccountSid;
  const experimentAccountSid = isSubAccount
    ? account.parentAccountSid
    : account?.sid;

  const splitConfig = useMemo<{
    core: { authorizationKey: string; key: string };
  }>(() => {
    const anonymousId = Segment.getAnonymousUserId();

    return {
      core: {
        authorizationKey: splitAuthorizationKey,
        key: experimentAccountSid || anonymousId,
      },
      /**
       * This configuration sends our feature flag evaluations to Datadog RUM
       * See: https://docs.datadoghq.com/real_user_monitoring/guide/setup-feature-flag-data-collection
       */
      impressionListener: {
        logImpression: (impressionData): void => {
          if (datadogRum?.getInitConfiguration()) {
            // verify to make sure datadogRum is available
            try {
              datadogRum.addFeatureFlagEvaluation(
                impressionData.impression.feature,
                impressionData.impression.treatment,
              );
            } catch (err) {
              logger.error(
                'Error while tracking split.io feature flag evaluation in datadogRum',
                {},
              );
            }
          }
        },
      },
    };
  }, [experimentAccountSid]);

  return (
    <SplitFactoryProvider
      config={splitConfig}
      attributes={{ unauthenticated: !experimentAccountSid }}
    >
      {children}
    </SplitFactoryProvider>
  );
};
